/**
 * DEV-269 — decisões PURAS do modo restrito do construtor de questionários
 * (spec 138, research.md R3). Sem React, sem SurveyJS: só funções sobre o
 * mapa `restrictions` que a API devolve. O binder
 * (`bind-alignment-restrictions.ts`) liga estas decisões aos eventos do
 * `SurveyCreator`; a última barreira é o Core (422).
 *
 * FR-010: nenhuma lista de páginas, perguntas ou protegidos vive aqui —
 * tudo é derivado de `restrictions` por `name`.
 */

export interface AlignmentRestrictionQuestion {
  name: string;
  protected: boolean;
}
export interface AlignmentRestrictionPage {
  name: string;
  protected: boolean;
  questions: AlignmentRestrictionQuestion[];
}
export interface AlignmentRestrictions {
  defaultPages: AlignmentRestrictionPage[];
}

export interface AlignmentClassifier {
  defaultPageNames: string[];
  /** Última página padrão — "Descritivos": sempre a última do formulário. */
  lastDefaultPageName: string | null;
  isDefaultPage(name: string | null | undefined): boolean;
  isProtectedPage(name: string | null | undefined): boolean;
  isDefaultQuestion(name: string | null | undefined): boolean;
  isProtectedQuestion(name: string | null | undefined): boolean;
  /** Página padrão em que a pergunta padrão nasceu; `null` para adicionais. */
  defaultPageOf(questionName: string | null | undefined): string | null;
}

export function classify(restrictions: AlignmentRestrictions): AlignmentClassifier {
  const pages = new Map<string, AlignmentRestrictionPage>();
  const questions = new Map<string, { protected: boolean; page: string }>();
  for (const page of restrictions.defaultPages) {
    pages.set(page.name, page);
    for (const q of page.questions)
      questions.set(q.name, { protected: q.protected, page: page.name });
  }
  const defaultPageNames = restrictions.defaultPages.map((p) => p.name);
  return {
    defaultPageNames,
    lastDefaultPageName: defaultPageNames[defaultPageNames.length - 1] ?? null,
    isDefaultPage: (name) => (name ? pages.has(name) : false),
    isProtectedPage: (name) => (name ? (pages.get(name)?.protected ?? false) : false),
    isDefaultQuestion: (name) => (name ? questions.has(name) : false),
    isProtectedQuestion: (name) => (name ? (questions.get(name)?.protected ?? false) : false),
    defaultPageOf: (name) => (name ? (questions.get(name)?.page ?? null) : null),
  };
}

// ---------------------------------------------------------------------------
// Níveis do painel de propriedades
// ---------------------------------------------------------------------------

export type PropertyGridLevel =
  | 'survey'
  | 'default-page'
  | 'additional-page'
  | 'default-question'
  | 'additional-question';

/** Objeto selecionado no construtor, reduzido ao que a decisão precisa. */
export interface SelectedObjectLike {
  type: string;
  name?: string | null;
}

export function levelFor(cls: AlignmentClassifier, obj: SelectedObjectLike): PropertyGridLevel {
  if (obj.type === 'survey') return 'survey';
  if (obj.type === 'page') return cls.isDefaultPage(obj.name) ? 'default-page' : 'additional-page';
  return cls.isDefaultQuestion(obj.name) ? 'default-question' : 'additional-question';
}

// ---------------------------------------------------------------------------
// Opções do construtor (AC-28, AC-28b, AC-29)
// ---------------------------------------------------------------------------

export interface AlignmentCreatorOptions {
  showDesignerTab: true;
  showPreviewTab: true;
  showLogicTab: true;
  showJSONEditorTab: false;
  showThemeTab: false;
  showTranslationTab: false;
  showSurveyTitle: false;
  allowEditSurveyTitle: false;
  /** Engrenagem "Configurações do criador" no rail de categorias — aparência do editor (SC-13 / AC-29b). */
  showCreatorThemeSettings: false;
  isAutoSave: false;
  pageEditMode: 'standard';
}

export function creatorOptionsForAlignment(): AlignmentCreatorOptions {
  return {
    showDesignerTab: true,
    showPreviewTab: true,
    showLogicTab: true,
    showJSONEditorTab: false,
    showThemeTab: false,
    showTranslationTab: false,
    showSurveyTitle: false,
    allowEditSurveyTitle: false,
    showCreatorThemeSettings: false,
    isAutoSave: false,
    pageEditMode: 'standard',
  };
}

// ---------------------------------------------------------------------------
// Barra de categorias (painéis do property grid) — AC-29b / AC-32b / AC-46b
// ---------------------------------------------------------------------------

/** Nomes de categoria do SurveyJS Creator (chaves de `pe.tabs`). */
const SURVEY_PANELS_KEPT = new Set(['pages', 'logic']);
const DEFAULT_ELEMENT_PANELS_KEPT = new Set(['general']);
const ADDITIONAL_PAGE_PANELS_HIDDEN = new Set(['layout', 'navigation']);
const ADDITIONAL_QUESTION_PANELS_HIDDEN = new Set(['layout']);

export function isPanelVisible(level: PropertyGridLevel, panelName: string): boolean {
  switch (level) {
    case 'survey':
      return SURVEY_PANELS_KEPT.has(panelName);
    case 'default-page':
    case 'default-question':
      return DEFAULT_ELEMENT_PANELS_KEPT.has(panelName);
    case 'additional-page':
      return !ADDITIONAL_PAGE_PANELS_HIDDEN.has(panelName);
    case 'additional-question':
      return !ADDITIONAL_QUESTION_PANELS_HIDDEN.has(panelName);
  }
}

// ---------------------------------------------------------------------------
// Controles do painel — AC-32 / AC-37
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_PROPERTIES = new Set(['visible']);
const DEFAULT_QUESTION_PROPERTIES = new Set(['visible', 'isRequired']);

export function isPropertyVisible(level: PropertyGridLevel, propertyName: string): boolean {
  if (level === 'default-page') return DEFAULT_PAGE_PROPERTIES.has(propertyName);
  if (level === 'default-question') return DEFAULT_QUESTION_PROPERTIES.has(propertyName);
  return true;
}

// ---------------------------------------------------------------------------
// Adorner (ações do elemento no canvas) — AC-33 / AC-36 / AC-38
// ---------------------------------------------------------------------------

export interface AllowOperations {
  allowChangeRequired?: boolean;
  allowChangeType?: boolean;
  allowChangeInputType?: boolean;
  allowCopy?: boolean;
  allowDelete?: boolean;
  allowDrag?: boolean;
  allowDragging?: boolean;
  allowEdit?: boolean;
  allowShowSettings?: boolean;
}

export function allowOperationsFor(level: PropertyGridLevel): AllowOperations {
  if (level === 'default-page') {
    return {
      allowDelete: false,
      allowCopy: false,
      allowDrag: false,
      allowDragging: false,
      allowEdit: false,
      allowShowSettings: true,
    };
  }
  if (level === 'default-question') {
    // `allowChangeRequired` fica true mesmo nas protegidas: a recusa com
    // explicação acontece em `guardPropertyChange` (AC-41 — "o controle não
    // fica alternado"), não escondendo a ação.
    return {
      allowChangeType: false,
      allowChangeInputType: false,
      allowCopy: false,
      allowDelete: false,
      allowDrag: false,
      allowDragging: false,
      allowEdit: false,
      allowChangeRequired: true,
      allowShowSettings: true,
    };
  }
  return {};
}

export const HIDE_ACTION_ID = 'alignment-hide';
/**
 * "É obrigatório?" inline da pergunta padrão é uma ação PRÓPRIA: a do Creator
 * (`isrequired`) só executa quando `allowEdit` é true, e o modo restrito o
 * desliga na pergunta padrão (AC-38) — a ação nativa vira um no-op silencioso.
 * A nossa passa pela mesma guarda do painel (AC-39/40, AC-41).
 */
export const REQUIRED_ACTION_ID = 'alignment-required';

/**
 * Ids das ações inline do Creator que FICAM; `null` = não filtrar (fica com
 * as ações padrão). Na pergunta padrão nenhuma fica: o binder acrescenta as
 * duas próprias ("É obrigatório?" e "Ocultar") — AC-36.
 */
export function inlineActionIdsFor(level: PropertyGridLevel): string[] | null {
  if (level === 'default-question') return [];
  return null;
}

// ---------------------------------------------------------------------------
// Ordem das páginas — AC-33b / AC-33c / AC-42
// ---------------------------------------------------------------------------

/**
 * Ordem aceita: as padrão na ordem canônica relativa; toda adicional depois
 * da 4ª padrão e antes da última ("Descritivos").
 */
export function isPageOrderAllowed(cls: AlignmentClassifier, order: readonly string[]): boolean {
  const defaults = order.filter((n) => cls.isDefaultPage(n));
  const expected = cls.defaultPageNames.filter((n) => defaults.includes(n));
  if (defaults.join('\u0000') !== expected.join('\u0000')) return false;

  const fourth = cls.defaultPageNames[3];
  const fourthIdx = fourth ? order.indexOf(fourth) : -1;
  const lastIdx = cls.lastDefaultPageName ? order.indexOf(cls.lastDefaultPageName) : -1;
  return order.every((name, i) => {
    if (cls.isDefaultPage(name)) return true;
    if (fourthIdx >= 0 && i < fourthIdx) return false;
    if (lastIdx >= 0 && i > lastIdx) return false;
    return true;
  });
}

/** Índice em que uma página nova deve entrar: logo antes de "Descritivos". */
export function insertionIndexForNewPage(
  cls: AlignmentClassifier,
  order: readonly string[]
): number {
  const lastIdx = cls.lastDefaultPageName ? order.indexOf(cls.lastDefaultPageName) : -1;
  return lastIdx >= 0 ? lastIdx : order.length;
}

/** Prefixo do nome gerado para página adicional (AC-42: `páginaN`). */
export const ADDITIONAL_PAGE_NAME_PREFIX = 'página';

/**
 * Nome de página adicional no padrão `páginaN`, com N = posição da página no
 * formulário (1-based) — o mesmo número que o canvas do Creator mostra como
 * rótulo ("Página N") quando a página não tem título. O Creator sozinho
 * numeraria pela contagem de nomes `página*` ("página1" numa 5ª página).
 * Nome já usado pula para o próximo livre.
 */
export function additionalPageName(taken: readonly string[], position: number): string {
  const used = new Set(taken);
  let n = Math.max(1, position + 1);
  while (used.has(`${ADDITIONAL_PAGE_NAME_PREFIX}${n}`)) n += 1;
  return `${ADDITIONAL_PAGE_NAME_PREFIX}${n}`;
}

// ---------------------------------------------------------------------------
// Solte de perguntas — AC-38b / AC-47 / AC-47b
// ---------------------------------------------------------------------------

export interface QuestionDropContext {
  /** `null` = pergunta nova vinda da paleta. */
  draggedName: string | null;
  /** Página onde a pergunta cairia; `null` quando não dá para resolver. */
  targetPage: string | null;
}

export function isQuestionDropAllowed(cls: AlignmentClassifier, ctx: QuestionDropContext): boolean {
  if (!ctx.targetPage) return false;
  if (cls.isDefaultQuestion(ctx.draggedName)) return false; // nunca sai do lugar (AC-38/38b)
  return !cls.isDefaultPage(ctx.targetPage); // página padrão não recebe pergunta (AC-47/47b)
}

// ---------------------------------------------------------------------------
// Mudança de propriedade — AC-41 / AC-41b / AC-33 / AC-38
// ---------------------------------------------------------------------------

export interface PropertyChangeContext {
  kind: 'page' | 'question';
  name: string | null | undefined;
  property: string;
  newValue: unknown;
}

export interface GuardResult {
  allow: boolean;
  message?: string;
}

export const PROTECTED_QUESTION_MESSAGE =
  'Esse campo identifica ou descreve a vaga: não pode ser ocultado nem deixar de ser obrigatório.';
export const PROTECTED_PAGE_MESSAGE =
  'Essa página contém campos que identificam ou descrevem a vaga e não pode ser ocultada.';
export const DEFAULT_ELEMENT_LOCKED_MESSAGE =
  'Elemento padrão do formulário: só a visibilidade (e a obrigatoriedade, nas perguntas) pode mudar.';

/**
 * Propriedades que a aba "Lógica" grava num elemento (visibleIf, enableIf,
 * requiredIf, resetValueIf, setValueIf, setValueExpression…): toda condição ou
 * expressão. Num elemento padrão são recusadas como qualquer edição fora de
 * visível/obrigatório, mas com uma explicação própria — QA 09/10 (SC-14).
 */
export function isLogicProperty(property: string): boolean {
  return /If$|Expression$/.test(property);
}

/** Explicação da recusa de uma regra de Lógica sobre um elemento padrão, nomeando-o (SC-14). */
export function logicRuleRefusalMessage(
  kind: 'page' | 'question',
  label: string,
  isProtected: boolean
): string {
  if (kind === 'page') {
    return `Regra de Lógica desfeita: a página "${label}" é padrão do formulário e não pode ser mostrada, escondida, ativada ou exigida por regra.`;
  }
  return isProtected
    ? `Regra de Lógica desfeita: "${label}" identifica ou descreve a vaga e não pode ser escondido, desativado nem ter a obrigatoriedade ou o valor mudados por regra.`
    : `Regra de Lógica desfeita: "${label}" é uma pergunta padrão do formulário e não pode ser escondida, desativada nem ter a obrigatoriedade ou o valor mudados por regra.`;
}

export function guardPropertyChange(
  cls: AlignmentClassifier,
  ctx: PropertyChangeContext
): GuardResult {
  if (ctx.kind === 'page') {
    if (!cls.isDefaultPage(ctx.name)) return { allow: true };
    if (ctx.property === 'visible') {
      if (ctx.newValue === false && cls.isProtectedPage(ctx.name)) {
        return { allow: false, message: PROTECTED_PAGE_MESSAGE };
      }
      return { allow: true };
    }
    return { allow: false, message: DEFAULT_ELEMENT_LOCKED_MESSAGE };
  }

  if (!cls.isDefaultQuestion(ctx.name)) return { allow: true };
  if (ctx.property === 'visible' || ctx.property === 'isRequired') {
    if (ctx.newValue === false && cls.isProtectedQuestion(ctx.name)) {
      return { allow: false, message: PROTECTED_QUESTION_MESSAGE };
    }
    return { allow: true };
  }
  return { allow: false, message: DEFAULT_ELEMENT_LOCKED_MESSAGE };
}

// ---------------------------------------------------------------------------
// Formulário inteiro — AC-35
// ---------------------------------------------------------------------------

export function hasVisiblePage(
  definition: { pages?: { visible?: boolean }[] } | null | undefined
): boolean {
  const pages = definition?.pages;
  if (!Array.isArray(pages) || pages.length === 0) return false;
  return pages.some((p) => p.visible !== false);
}
